// src/components/Dropdown.jsx
// Reusable dropdown component for selecting from a list of options
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import { useTheme } from '../context/ThemeContext';

const Dropdown = ({
  label,
  value,
  placeholder = 'Select...',
  options = [],
  onSelect,
  className = '',
  disabled = false,
}) => {
  const { fontSize } = useGlobalFontSize();
  const dropdownFontSize = Math.max(10, Number(fontSize) - 2);
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onSelect(optionValue);
    setIsOpen(false);
  };

  // Find the selected option's label
  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className={`flex flex-col gap-[8px] items-start relative ${className}`}>
      {label && (
        <label 
          className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]"
          style={{ fontSize: dropdownFontSize }}
        >
          {label}
        </label>
      )}
      <div className="relative w-full" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full border border-bloom-green rounded-[8px] h-[32px] px-[12px] py-[8px] flex items-center justify-between gap-2 transition-opacity bg-white dark:bg-[#282a29] ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <span
            title={displayText}
            className={`font-['Inter'] font-medium flex-1 min-w-0 text-left overflow-x-auto whitespace-nowrap ${
              value
                ? (isDark ? 'text-[#f0f5f2]' : 'text-[#282A29]')
                : 'text-[#5d625f]'
            }`}
            style={{ fontSize: dropdownFontSize }}
          >
            {displayText}
          </span>
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? 'rotate-180' : ''} text-[#282A29] dark:text-[#f0f5f2]`}
          />
        </button>
        {isOpen && !disabled && (
          <div className="absolute top-full left-0 z-[110] mt-1 max-h-[200px] w-max min-w-full max-w-[80vw] overflow-y-auto rounded-[8px] border border-bloom-green bg-white dark:bg-[#282a29]">
            {options.length === 0 ? (
              <div className="px-[12px] py-[8px]">
                <span 
                  className="font-['Inter'] font-medium text-[#5d625f]"
                  style={{ fontSize: dropdownFontSize }}
                >
                  No options available
                </span>
              </div>
            ) : (
              options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className="w-full px-[12px] py-[8px] text-left transition-colors hover:bg-[#F0F5F2] dark:hover:bg-[#1d1d1d]"
                >
                  <span 
                    className="block font-['Inter'] font-medium text-[#282A29] dark:text-[#f0f5f2] whitespace-nowrap"
                    style={{ fontSize: dropdownFontSize }}
                  >
                    {option.label}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
